import { View } from 'react-native';
import React, { useMemo } from 'react';

import { LocalisedServerValue } from '@components/LocalisedServerValue';
import { OrderMessageCategory } from '@lib/types';
import { ReceiptRow } from '@components/ReceiptRow';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useTranslation } from '@providers/I18n';

import { calculateItemTotalPrice } from './utils';

type OrderItemCardProps = {
  category: OrderMessageCategory | any;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.base[100],
    elevation: 8,
    borderWidth: 2,
    borderColor: '#EFF0F6',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  category: {
    paddingBottom: theme.spacing(1),
  },
  pb4: {
    paddingBottom: theme.spacing(2),
  },
  py4: {
    paddingVertical: theme.spacing(2),
  },
  item: {
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey[400],
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: theme.spacing(2),
  },
  name: {
    flex: 1,
  },
  quatity: {
    flex: 1,
    textAlign: 'center',
  },
  price: {
    flex: 1,
    textAlign: theme.direction === 'rtl' ? 'left' : 'right',
  },
}));

export const OrderItemCard: React.FC<OrderItemCardProps> = ({ category }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const itemTotal = useMemo(() => {
    return calculateItemTotalPrice(category) || 0;
  }, [category]);

  const items = (category?.items || []).map((item: any, i: number) => (
    <View
      testID={`item-data-${i}`}
      style={i === (category?.items?.length || 0) - 1 ? styles.py4 : styles.item}
      key={'item' + i}>
      
      <View style={styles.title}>
        <Typography
          testID="item-name"
          weight="bold"
          size={{ xs: 'sm', lg: 'md' }}
          style={styles.name}>
          <LocalisedServerValue value={item?.item} />
        </Typography>
        
        <Typography
          testID="quantity"
          weight="bold"
          size={{ xs: 'sm', lg: 'md' }}
          style={styles.quatity}>
          x{item?.quantity || 0} ({(item?.price || 0).toFixed(2)} {t('common.egp', 'EGP')})
        </Typography>
        
        <Typography
          testID="price"
          weight="bold"
          size={{ xs: 'sm', lg: 'md' }}
          style={styles.price}>
          {((item?.quantity || 0) * (item?.price || 0)).toFixed(2)} {t('common.egp')}
        </Typography>
      </View>

      <Typography
        testID="size"
        weight="bold"
        size={{ xs: 'sm', lg: 'md' }}
        variant="primary">
        {t('orderDetails.size', 'Size')}:
      </Typography>
      <Typography
        testID="item-size"
        weight="bold"
        size="sm"
        variant="primary"
        style={styles.pb4}>
        <LocalisedServerValue value={item?.size} />
      </Typography>

      {(item?.item?.topping?.length ?? 0) > 0 && (
        <Typography
          testID="extras"
          weight="bold"
          size={{ xs: 'sm', lg: 'md' }}
          variant="primary">
          {t('orderDetails.extras', 'Extras')}:
        </Typography>
      )}

      {item?.item?.topping?.map((topping: any, idx: number) =>
        topping?.extraItems?.map((extraItem: any, j: number) => (
          <ReceiptRow
            key={`extra-${idx}-${j}`}
            name={<LocalisedServerValue value={extraItem?.item} />}
            value={`${
              ((item?.quantity || 0) * (extraItem?.price || 0)).toFixed(2)
            } ${t('common.egp')}`}
            title={
              <LocalisedServerValue value={topping?.category} />
            }
            testID="extra-row"
            TypographyProps={{
              size: 'sm',
            }}
          />
        )),
      )}

      {item?.item?.comment && (
        <>
          <Typography
            testID="comments"
            weight="bold"
            size={{ xs: 'sm', lg: 'md' }}
            variant="primary">
            {t('orderDetails.comments', 'Comments')}:
          </Typography>
          <Typography
            testID="item-comment"
            weight="extraBold"
            size="sm"
            variant="primary"
            style={styles.pb4}>
            {item.item.comment}
          </Typography>
        </>
      )}
    </View>
  ));

  return (
    <View style={styles.root}>
      <Typography
        testID="category-name"
        weight="bold"
        size={{ xs: 'sm', lg: 'md' }}>
        <LocalisedServerValue value={category} />
      </Typography>
      
      {items}
      
      <ReceiptRow
        name={t('orderDetails.total', 'Total')}
        value={itemTotal.toFixed(2) + t('common.egp')}
        testID="item-total"
        TypographyProps={{
          weight: 'extraBold',
          variant: 'primary',
        }}
      />
    </View>
  );
};
